import React, { FC } from "react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ProjectedPaymentsForm } from "../services";
import TableComponent from 'components/Table';
import InputCell from "./input-cell";
import { Box, IconButton, Paper, Typography, useTheme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextButton } from "components/TextButton";
import NumberField from "components/number-field";
import { useIsMobile } from "./use-projected-payments-viewport";

const useStyles = makeStyles((theme: any) => ({
  centerText: {
    textAlign: "center",
    '&>div>input': {
      textAlign: "center",
    }
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderColor: theme.palette.border.line,
  },
  rangeRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: theme.spacing(1),
  },
  rangeInput: {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  touchButton: {
    minWidth: 44,
    minHeight: 44,
  },
  addWrapper: {
    minHeight: 44,
  },
  desktopAddWrapper: {
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: 44 },
    },
  },
  touchTarget: {
    padding: 0,
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: 44,
      minHeight: 44,
    },
  },
}));

type Props = {
  methods: UseFormReturn<ProjectedPaymentsForm>,
}

export const YearsRangesTable: FC<Props> = ({ methods }) => {
  const { t } = useTranslation();
  const {
    fields: yearRangesFields,
    append,
    remove
  } = useFieldArray({
    control: methods.control,
    name: "year_ranges",
  });
  const classes = useStyles();
  const theme = useTheme<any>();
  const isMobile = useIsMobile();

  const addButton = (
    <TextButton
      text={t('common:add')}
      onClick={() => append({
        range: [0, 0],
        entryId: "",
        amount: 0
      })}
      textColor={theme.palette.secondary.main}
      icon="plus"
      disabled={yearRangesFields.length >= 4}
      customStyle={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}
    />
  );

  if (isMobile) {
    return (
      <Box display="flex" flexDirection="column" minWidth={0} style={{ gap: theme.spacing(1.5) }}>
        <Box className={classes.addWrapper} display="flex" alignItems="center">
          {addButton}
        </Box>
        {yearRangesFields.map((row, index) => (
          <Paper
            key={row.id}
            variant="outlined"
            role="group"
            aria-label={`${t('projectedPayments:range')} ${index + 1}`}
            className={classes.card}
          >
            <Box minWidth={0}>
              <Typography variant="caption" color="textSecondary" component="div">
                {t('projectedPayments:range')}
              </Typography>
              <Box className={classes.rangeRow}>
                <Box className={classes.rangeInput}>
                  <Controller
                    control={methods.control}
                    name={`year_ranges.${index}.range.0`}
                    render={({ field }) => (
                      <InputCell
                        value={field.value || 0}
                        onChangeInput={(e: any) => {
                          field.onChange(Number(e.target.value));
                        }}
                        ariaLabel={`${t('projectedPayments:range')} ${index + 1} ${t('projectedPayments:min')}`}
                        inputClass={classes.centerText}
                      />
                    )}
                  />
                </Box>
                <span>-</span>
                <Box className={classes.rangeInput}>
                  <Controller
                    control={methods.control}
                    name={`year_ranges.${index}.range.1`}
                    render={({ field }) => (
                      <InputCell
                        value={field.value || 0}
                        onChangeInput={(e: any) => {
                          field.onChange(Number(e.target.value));
                        }}
                        ariaLabel={`${t('projectedPayments:range')} ${index + 1} ${t('projectedPayments:max')}`}
                        inputClass={classes.centerText}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
            <Box minWidth={0}>
              <Typography variant="caption" color="textSecondary" component="div">
                {t('projectedPayments:mortgageInsurance')}
              </Typography>
              <Controller
                control={methods.control}
                name={`year_ranges.${index}.amount`}
                render={({ field }) => (
                  <NumberField
                    color="secondary"
                    label={t('orders:amountLabel')}
                    name="amount"
                    handleBlur={field.onBlur}
                    value={field.value}
                    onValueChange={({ floatValue }) => field.onChange(floatValue)}
                    prefix="$"
                    thousandSeparator
                    fullWidth
                    disabled={methods.getValues(`year_ranges.${index}.entryId`) === ""}
                  />
                )}
              />
            </Box>
            <Box className={classes.cardFooter}>
              <IconButton
                className={classes.touchButton}
                aria-label={t('common:remove')}
                onClick={() => {
                  remove(index);
                }}
                size="large"
              >
                <FontAwesomeIcon icon="trash" size="xs" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box justifyItems="flex-end">
      <Box className={classes.desktopAddWrapper}>{addButton}</Box>
      <TableComponent
        disabledBorderLeft
        cellAlignment="left"
        columns={{
          actions: {
            header: { title: t('common:actionsHeader'), align: 'center', width: '80px' },
            renderer: ({ indexRow = 0 }) => (
              <Box display="flex" justifyContent="center">
                <IconButton
                  className={classes.touchTarget}
                  aria-label={t('common:remove')}
                  onClick={() => {
                    remove(indexRow);
                  }}
                  size="large"
                >
                  <FontAwesomeIcon icon="trash" size="xs" />
                </IconButton>
              </Box>
            ),
          },
          yearsRange: {
            header: { title: t('projectedPayments:range'), align: 'left' },
            renderer: ({ indexRow = 0, row }) => {
              return (
                <Box display="flex" style={{ gap: "10px", justifyContent: "flex-start", alignItems: "center" }} key={row.id}>
                  <Box width="50px" style={{ textAlign: "center" }}>
                    <Controller
                      control={methods.control}
                      name={`year_ranges.${indexRow}.range.0`}
                      render={({ field }) => (
                        <InputCell
                          value={field.value || 0}
                          onChangeInput={(e: any) => {
                            field.onChange(Number(e.target.value));
                          }}
                          ariaLabel={`${t('projectedPayments:range')} ${indexRow + 1} ${t('projectedPayments:min')}`}
                          inputClass={classes.centerText}
                        />
                      )}
                    />
                  </Box>
                  <span>-</span>
                  <Box width="50px">
                    <Controller
                      control={methods.control}
                      name={`year_ranges.${indexRow}.range.1`}
                      render={({ field }) => (
                        <InputCell
                          value={field.value || 0}
                          onChangeInput={(e: any) => {
                            field.onChange(Number(e.target.value));
                          }}
                          ariaLabel={`${t('projectedPayments:range')} ${indexRow + 1} ${t('projectedPayments:max')}`}
                          inputClass={classes.centerText}
                        />
                      )}
                    />
                  </Box>
                </Box>);
            },
          },
          amount: {
            header: { title: t('projectedPayments:mortgageInsurance'), align: 'left' },
            renderer: ({ indexRow = 0, row }) => {
              return (
                <Box display="flex" style={{ gap: "10px", justifyContent: "flex-start", alignItems: "center" }} key={row.id}>
                  <Controller
                    control={methods.control}
                    name={`year_ranges.${indexRow}.amount`}
                    render={({ field }) => (
                      <NumberField
                        color="secondary"
                        label={t('orders:amountLabel')}
                        name="amount"
                        handleBlur={field.onBlur}
                        value={field.value}
                        onValueChange={({ floatValue }) => field.onChange(floatValue)}
                        prefix="$"
                        thousandSeparator
                        disabled={methods.getValues(`year_ranges.${indexRow}.entryId`) === ""}
                      />
                    )}
                  />
                </Box>);
            },
          }
        }}
        dataSet={yearRangesFields}
      />
    </Box>
  );
};
